// 🔥 가격 백업 탭 컴포넌트 - 프리미엄급 UI
// [배경] 관리자 페이지에서 가격 데이터의 스냅샷 백업 목록 조회, 생성, 삭제 기능 제공
// BackupManagementPage 내 '가격 백업' 탭으로 사용

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  Grid,
  LinearProgress,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Collapse
} from '@mui/material';
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  CloudDownload as DownloadIcon,
  Analytics as AnalyticsIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  Headphones as HeadphonesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';

import { PriceSnapshotService } from '../../../../services/priceSnapshotService';
import {
  PriceSnapshot,
  SnapshotBreakdown,
  ValidationStatus,
  CreateSnapshotRequest
} from '../../../../types/priceSnapshot';
import PriceRestoreWizard from './PriceRestoreWizard';

// ============================================================
// 📊 타입 정의
// ============================================================

interface PriceBackupTabProps {
  /** 관리자 ID (스냅샷 생성 시 사용) */
  adminId?: string;
}

// ============================================================
// 🎨 스타일 상수
// ============================================================

const COLORS = {
  primary: '#1976d2',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  apple: '#555555',
  samsung: '#1428a0'
};

// ============================================================
// 🔧 유틸리티 함수
// ============================================================

/**
 * 파일 크기 포맷팅
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * 날짜 포맷팅
 */
const formatDate = (date: Timestamp | Date | string): string => {
  const d = date instanceof Timestamp ? date.toDate() : new Date(date);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * 검증 상태에 따른 칩 색상
 */
const getValidationChip = (status: ValidationStatus): { color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactNode } => {
  switch (status) {
    case 'valid':
      return { color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
    case 'warning':
      return { color: 'warning', icon: <WarningIcon fontSize="small" /> };
    case 'error':
      return { color: 'error', icon: <ErrorIcon fontSize="small" /> };
    default:
      return { color: 'default', icon: <InfoIcon fontSize="small" /> };
  }
};

// ============================================================
// 📦 메인 컴포넌트
// ============================================================

const PriceBackupTab: React.FC<PriceBackupTabProps> = ({ adminId = 'admin' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 상태 관리
  const [snapshots, setSnapshots] = useState<PriceSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // 다이얼로그 상태
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<PriceSnapshot | null>(null);
  const [newSnapshotDescription, setNewSnapshotDescription] = useState('');

  // 복구 마법사 상태
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSnapshot, setWizardSnapshot] = useState<PriceSnapshot | null>(null);

  // 확장된 행 상태
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // 알림 상태
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // ============================================================
  // 📋 데이터 로드
  // ============================================================

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PriceSnapshotService.getSnapshots({
        limit: 50,
        orderBy: 'createdAt',
        orderDirection: 'desc'
      });
      setSnapshots(data);
      setSnackbar({
        open: true,
        message: `가격 스냅샷 ${data.length}개를 불러왔습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('스냅샷 목록 조회 실패:', error);
      setSnackbar({
        open: true,
        message: '스냅샷 목록을 불러오는 데 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  // ============================================================
  // 📸 스냅샷 생성
  // ============================================================

  const handleCreateSnapshot = async () => {
    if (!newSnapshotDescription.trim()) {
      setSnackbar({
        open: true,
        message: '백업 설명을 입력해주세요.',
        severity: 'warning'
      });
      return;
    }

    setCreating(true);
    try {
      const request: CreateSnapshotRequest = {
        description: newSnapshotDescription.trim(),
        adminId
      };

      const result = await PriceSnapshotService.createSnapshot(request);

      if (result.success) {
        setSnackbar({
          open: true,
          message: `스냅샷이 성공적으로 생성되었습니다. (${result.durationMs}ms)`,
          severity: 'success'
        });
        setCreateDialogOpen(false);
        setNewSnapshotDescription('');
        fetchSnapshots();
      } else {
        throw new Error(result.error || '스냅샷 생성 실패');
      }
    } catch (error) {
      console.error('스냅샷 생성 실패:', error);
      setSnackbar({
        open: true,
        message: `스냅샷 생성 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  // ============================================================
  // 🗑️ 스냅샷 삭제
  // ============================================================

  const handleDeleteSnapshot = async () => {
    if (!selectedSnapshot) return;

    setDeleting(selectedSnapshot.id);
    try {
      await PriceSnapshotService.deleteSnapshot(selectedSnapshot.id);
      setSnackbar({
        open: true,
        message: '스냅샷이 삭제되었습니다.',
        severity: 'success'
      });
      setDeleteDialogOpen(false);
      setSelectedSnapshot(null);
      fetchSnapshots();
    } catch (error) {
      console.error('스냅샷 삭제 실패:', error);
      setSnackbar({
        open: true,
        message: '스냅샷 삭제에 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setDeleting(null);
    }
  };

  // ============================================================
  // 🔄 복구 마법사 열기
  // ============================================================

  const handleOpenRestoreWizard = (snapshot: PriceSnapshot) => {
    setWizardSnapshot(snapshot);
    setWizardOpen(true);
  };

  const handleWizardClose = (restored: boolean) => {
    setWizardOpen(false);
    setWizardSnapshot(null);
    if (restored) {
      setSnackbar({
        open: true,
        message: '데이터 복구가 완료되었습니다.',
        severity: 'success'
      });
    }
  };

  // ============================================================
  // 📊 행 확장 토글
  // ============================================================

  const toggleRowExpansion = (snapshotId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(snapshotId)) {
        next.delete(snapshotId);
      } else {
        next.add(snapshotId);
      }
      return next;
    });
  };

  // ============================================================
  // 📊 통계 계산
  // ============================================================

  const stats = {
    total: snapshots.length,
    completed: snapshots.filter(s => s.status === 'completed').length,
    totalRecords: snapshots.reduce((sum, s) => sum + s.totalPrices, 0),
    totalSize: snapshots.reduce((sum, s) => sum + s.fileSize, 0)
  };

  // ============================================================
  // 🎨 렌더링: 브레이크다운 표시
  // ============================================================

  const renderBreakdown = (breakdown: SnapshotBreakdown) => (
    <Grid container spacing={1} sx={{ mt: 1 }}>
      {/* Apple */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="caption" fontWeight="bold" sx={{ color: COLORS.apple }}>
            Apple
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<SmartphoneIcon sx={{ fontSize: 14 }} />}
              label={breakdown['Apple-smartphone']}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<TabletIcon sx={{ fontSize: 14 }} />}
              label={breakdown['Apple-tablet']}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<HeadphonesIcon sx={{ fontSize: 14 }} />}
              label={breakdown['Apple-wearable']}
              variant="outlined"
            />
          </Stack>
        </Box>
      </Grid>
      
      {/* Samsung */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="caption" fontWeight="bold" sx={{ color: COLORS.samsung }}>
            Samsung
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<SmartphoneIcon sx={{ fontSize: 14 }} />}
              label={breakdown['Samsung-smartphone']}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<TabletIcon sx={{ fontSize: 14 }} />}
              label={breakdown['Samsung-tablet']}
              variant="outlined"
            />
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );

  // ============================================================
  // 🎨 렌더링
  // ============================================================

  return (
    <Box>
      {/* 헤더 통계 카드 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <StorageIcon sx={{ fontSize: 32, color: COLORS.primary, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                총 스냅샷
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: COLORS.success, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                완료된 백업
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AnalyticsIcon sx={{ fontSize: 32, color: COLORS.info, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {stats.totalRecords.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                총 가격 레코드
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <DownloadIcon sx={{ fontSize: 32, color: COLORS.warning, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {formatFileSize(stats.totalSize)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                총 데이터 크기
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 액션 버튼 */}
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={creating}
          sx={{ py: 1.5 }}
        >
          {creating ? '생성 중...' : '새 스냅샷 생성'}
        </Button>

        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={fetchSnapshots}
          disabled={loading}
          sx={{ py: 1.5 }}
        >
          {loading ? '로딩 중...' : '새로고침'}
        </Button>
      </Stack>

      {/* 스냅샷 목록 테이블 */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 600,
          '& .MuiTableCell-root': {
            fontSize: { xs: '0.75rem', md: '0.875rem' }
          }
        }}
      >
        <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>스냅샷 정보</TableCell>
              {!isSmallMobile && <TableCell>상태</TableCell>}
              <TableCell align="right">데이터</TableCell>
              {!isMobile && <TableCell align="right">크기</TableCell>}
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshots.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <StorageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    저장된 가격 스냅샷이 없습니다.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    '새 스냅샷 생성' 버튼을 클릭하여 첫 번째 백업을 만들어보세요.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              snapshots.map((snapshot) => {
                const isExpanded = expandedRows.has(snapshot.id);
                const validationChip = getValidationChip(snapshot.validationStatus);

                return (
                  <React.Fragment key={snapshot.id}>
                    <TableRow hover>
                      {/* 확장 버튼 */}
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowExpansion(snapshot.id)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>

                      {/* 스냅샷 정보 */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {snapshot.description || '설명 없음'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {snapshot.id.slice(-16)}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(snapshot.createdAt)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* 상태 */}
                      {!isSmallMobile && (
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              label={snapshot.status === 'completed' ? '완료' : snapshot.status === 'failed' ? '실패' : '진행중'}
                              color={snapshot.status === 'completed' ? 'success' : snapshot.status === 'failed' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              icon={validationChip.icon}
                              label={snapshot.validationStatus === 'valid' ? '검증OK' : snapshot.validationStatus}
                              color={validationChip.color}
                              variant="outlined"
                            />
                          </Stack>
                        </TableCell>
                      )}

                      {/* 데이터 수 */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {snapshot.totalPrices.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          가격 항목
                        </Typography>
                      </TableCell>

                      {/* 크기 */}
                      {!isMobile && (
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatFileSize(snapshot.fileSize)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {snapshot.chunkCount}개 청크
                          </Typography>
                        </TableCell>
                      )}

                      {/* 작업 버튼 */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="복구 마법사">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenRestoreWizard(snapshot)}
                              disabled={snapshot.status !== 'completed'}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="삭제">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedSnapshot(snapshot);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={deleting === snapshot.id}
                            >
                              {deleting === snapshot.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* 확장된 상세 정보 */}
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                              브랜드/디바이스별 분포
                            </Typography>
                            {renderBreakdown(snapshot.breakdown)}

                            {snapshot.validationWarnings && snapshot.validationWarnings.length > 0 && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>검증 경고</AlertTitle>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {snapshot.validationWarnings.map((w, i) => (
                                    <li key={i}>{w}</li>
                                  ))}
                                </ul>
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 스냅샷 생성 다이얼로그 */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BackupIcon color="primary" />
            <Typography variant="h6">새 가격 스냅샷 생성</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>백업 안내</AlertTitle>
            현재 설정된 모든 가격 데이터(스마트폰, 태블릿, 웨어러블)를 스냅샷으로 저장합니다.
            이 백업은 나중에 복구 마법사를 통해 전체 또는 부분 복구할 수 있습니다.
          </Alert>

          <TextField
            fullWidth
            label="백업 설명"
            placeholder="예: 2024년 12월 정기 백업, 가격 대폭 변경 전 백업"
            value={newSnapshotDescription}
            onChange={(e) => setNewSnapshotDescription(e.target.value)}
            multiline
            rows={2}
            disabled={creating}
            helperText="나중에 복구할 때 쉽게 식별할 수 있도록 설명을 입력해주세요."
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSnapshot}
            disabled={creating || !newSnapshotDescription.trim()}
            startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
          >
            {creating ? '생성 중...' : '스냅샷 생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">스냅샷 삭제</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>주의</AlertTitle>
            이 작업은 되돌릴 수 없습니다. 스냅샷과 관련 데이터가 영구적으로 삭제됩니다.
          </Alert>

          {selectedSnapshot && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold">
                  {selectedSnapshot.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  생성: {formatDate(selectedSnapshot.createdAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  데이터: {selectedSnapshot.totalPrices.toLocaleString()}개 가격 항목
                </Typography>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={!!deleting}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSnapshot}
            disabled={!!deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? '삭제 중...' : '삭제 확인'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 복구 마법사 */}
      {wizardSnapshot && (
        <PriceRestoreWizard
          open={wizardOpen}
          snapshot={wizardSnapshot}
          adminId={adminId}
          onClose={handleWizardClose}
        />
      )}

      {/* 스낵바 알림 (간단한 대체) */}
      <Fade in={snackbar.open}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 300,
            boxShadow: 6
          }}
        >
          {snackbar.message}
        </Alert>
      </Fade>
    </Box>
  );
};

export default PriceBackupTab;

